<template>
  <div class="page-container animate-fade-in">
    <PageHeader
      title="Portfolio Management"
      description="Manage and organize your portfolio projects."
      :breadcrumbs="[
        { label: 'Admin', to: '/admin' },
        { label: 'Portfolio' },
      ]"
    >
      <template #actions>
        <BaseButton variant="primary" tag="router-link" to="/admin/portfolio/create">
          <AppIcon name="Plus" :size="16" />
          Add Project
        </BaseButton>
      </template>
    </PageHeader>

    <BaseCard :padding="false" class="overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead class="bg-border-subtle/50 border-b border-border sticky top-0">
            <tr>
              <th class="text-left px-6 py-4 font-medium text-muted">Preview</th>
              <th class="text-left px-6 py-4 font-medium text-muted">Project</th>
              <th class="text-left px-6 py-4 font-medium text-muted hidden md:table-cell">Category</th>
              <th class="text-left px-6 py-4 font-medium text-muted hidden lg:table-cell">Status</th>
              <th class="text-right px-6 py-4 font-medium text-muted">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-border-subtle">
            <tr
              v-for="item in paginatedItems"
              :key="item.id"
              class="hover:bg-border-subtle/30 transition-colors"
            >
              <td class="px-6 py-4">
                <div class="flex items-center justify-center w-12 h-12 rounded-lg bg-primary-muted text-primary">
                  <AppIcon :name="item.file_type === 'apk' ? 'Smartphone' : 'FileArchive'" :size="24" />
                </div>
              </td>
              <td class="px-6 py-4">
                <p class="font-medium">{{ item.file_name }}</p>
                <BaseBadge variant="primary" size="sm" class="mt-1 uppercase">{{ item.file_type }}</BaseBadge>
              </td>
              <td class="px-6 py-4 hidden md:table-cell">
                <BaseBadge variant="default">{{ item.category }}</BaseBadge>
              </td>
              <td class="px-6 py-4 hidden lg:table-cell">
                <BaseBadge :variant="item.publish ? 'success' : 'warning'" size="sm">
                  {{ item.publish ? 'Published' : 'Hidden' }}
                </BaseBadge>
              </td>
              <td class="px-6 py-4">
                <div class="flex items-center justify-end gap-1">
                  <button
                    type="button"
                    title="Toggle publish"
                    class="p-2 rounded-lg text-muted hover:bg-border-subtle transition-colors"
                    @click="togglePublish(item)"
                  >
                    <AppIcon :name="item.publish ? 'Eye' : 'Eye'" :size="16" />
                  </button>
                  <button
                    type="button"
                    title="Delete"
                    class="p-2 rounded-lg text-danger hover:bg-danger/10 transition-colors"
                    @click="removeItem(item.id)"
                  >
                    <AppIcon name="Trash2" :size="16" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex items-center justify-between px-6 py-4 border-t border-border-subtle">
        <p class="text-caption">
          Showing {{ (currentPage - 1) * perPage + 1 }}–{{ Math.min(currentPage * perPage, items.length) }} of
          {{ items.length }}
        </p>
        <div class="flex items-center gap-2">
          <BaseButton variant="secondary" size="sm" :disabled="currentPage === 1" @click="currentPage--">
            Previous
          </BaseButton>
          <span class="text-sm text-muted px-2">{{ currentPage }} / {{ totalPages }}</span>
          <BaseButton variant="secondary" size="sm" :disabled="currentPage >= totalPages" @click="currentPage++">
            Next
          </BaseButton>
        </div>
      </div>
    </BaseCard>
  </div>
</template>

<script>
import { siteState, saveSiteSection } from '@/composables/useSiteData'
import { useToast } from '@/composables/useToast'
import PageHeader from '@/components/layout/PageHeader.vue'
import BaseCard from '@/components/ui/BaseCard.vue'
import BaseBadge from '@/components/ui/BaseBadge.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import AppIcon from '@/components/ui/AppIcon.vue'

export default {
  name: 'AdminPortfolio',
  components: { PageHeader, BaseCard, BaseBadge, BaseButton, AppIcon },
  data() {
    return {
      currentPage: 1,
      perPage: 5,
    }
  },
  computed: {
    items() {
      return siteState.portfolioItems
    },
    totalPages() {
      return Math.ceil(this.items.length / this.perPage) || 1
    },
    paginatedItems() {
      const start = (this.currentPage - 1) * this.perPage
      return this.items.slice(start, start + this.perPage)
    },
  },
  methods: {
    async persist() {
      await saveSiteSection('portfolioItems', siteState.portfolioItems)
    },
    async togglePublish(item) {
      item.publish = !item.publish
      const { success, error } = useToast()
      try {
        await this.persist()
        success('Updated', `${item.file_name} is now ${item.publish ? 'published' : 'hidden'}.`)
      } catch (e) {
        error('Update failed', 'Could not update portfolio item.')
      }
    },
    async removeItem(id) {
      if (!window.confirm('Delete this portfolio item?')) return
      const index = siteState.portfolioItems.findIndex((item) => item.id === id)
      if (index === -1) return
      siteState.portfolioItems.splice(index, 1)
      const { success, error } = useToast()
      try {
        await this.persist()
        success('Deleted', 'Portfolio item removed.')
      } catch (e) {
        error('Delete failed', 'Could not delete portfolio item.')
      }
    },
  },
}
</script>
